import { ComponentType, Suspense, lazy, useEffect } from 'react'
import { BrowserRouter, Route, Routes, matchPath, useLocation } from 'react-router'
import Stack from '@mui/material/Stack'
import { getBasePath } from '../env.config'
import MainLayout from '../layout/main.layout'
import UserLayout from '../layout/user.layout'
import AuthGuard from './auth-guard.component'
import GuestGuard from './guest-guard.component'
import { ALL_ROUTES, PUBLIC_ROUTES, USER_ROUTES } from './routes.config'
import { analytics } from '@/core/analytics/analytics'
import ErrorBoundary from '../components/error-boundary.component'
import ScrollToTop from './scroll-to-top.component'
import { PageLoadingOverlay } from '../components/Loader/loading-bar.component'

// Patterns with a param are tried last: `/:referralCode` is a catch-all deep link that
// would otherwise swallow single-segment paths like /user — the same ranking the router
// applies to its own routes. sort() is stable, so declaration order holds within a group.
const ROUTE_PATTERNS = ALL_ROUTES.map(route => route.path).sort((a, b) => Number(a.includes(':')) - Number(b.includes(':')))

// GA4's page_view only fires on the first load, so client-side navigations were invisible
// to the journey trace. `page_route` is the matched route PATTERN, which stays a usable
// low-cardinality GA4 dimension where the raw path does not, and tells pages apart where
// the document title ("Feel Great" everywhere) cannot. Path only — the query string
// carries referral ids.
function PageViewTracker() {
    const { pathname } = useLocation()

    useEffect(() => {
        analytics.track('pageViewed', {
            page_path: pathname,
            page_route: ROUTE_PATTERNS.find(pattern => matchPath(pattern, pathname)) ?? 'unmatched'
        })
    }, [pathname])

    return null
}

const pageModules = import.meta.glob('../../pages/**/*.page.tsx')

const lazyPageCache = new Map<string, React.LazyExoticComponent<ComponentType>>()

function getLazyPage(component: string): React.LazyExoticComponent<ComponentType> | null {
    if (lazyPageCache.has(component)) return lazyPageCache.get(component)!

    const path = `../../pages/${component}.tsx`
    const importFn = pageModules[path]
    if (!importFn) return null

    const LazyComponent = lazy(importFn as () => Promise<{ default: ComponentType }>)
    lazyPageCache.set(component, LazyComponent)
    return LazyComponent
}

function DynamicPageWrapper({ component }: { component: string }) {
    const Component = getLazyPage(component)

    if (!Component) {
        return <div data-testid="router-div-page-not-found">Page not found: {component}</div>
    }

    return (
        <ErrorBoundary fallback="page" resetKey={component} data-testid="router-error-boundary">
            <Suspense fallback={<PageLoadingOverlay />} data-testid="router-suspense-1">
                <Component data-testid="router-component" />
            </Suspense>
        </ErrorBoundary>
    )
}

const AppRouter = () => {
    const basePath = getBasePath()

    return (
        <BrowserRouter basename={basePath} data-testid="router-browser-router">
            <ScrollToTop data-testid="router-scroll-to-top" />
            <PageViewTracker />
            <MainLayout data-testid="router-main-layout">
                <Suspense fallback={<PageLoadingOverlay />} data-testid="router-suspense-2">
                    <Routes data-testid="router-routes">
                        {PUBLIC_ROUTES.map(route => {
                            const page = (
                                <Stack data-testid="router-stack-dynamic-page-wrapper">
                                    <DynamicPageWrapper component={route.page} data-testid="router-dynamic-page-wrapper-1" />
                                </Stack>
                            )
                            return (
                                <Route
                                    key={route.path}
                                    path={route.path}
                                    element={route.guestOnly ? <GuestGuard data-testid="router-guest-guard">{page}</GuestGuard> : page}
                                    data-testid="router-route-1"
                                />
                            )
                        })}

                        <Route
                            element={
                                <AuthGuard data-testid="router-auth-guard">
                                    <UserLayout data-testid="router-user-layout" />
                                </AuthGuard>
                            }
                            data-testid="router-route-2">
                            {USER_ROUTES.map(route => (
                                <Route
                                    key={route.path}
                                    path={route.path}
                                    element={<DynamicPageWrapper component={route.page} data-testid="router-dynamic-page-wrapper-2" />}
                                    data-testid="router-route-3"
                                />
                            ))}
                        </Route>
                    </Routes>
                </Suspense>
            </MainLayout>
        </BrowserRouter>
    )
}

export default AppRouter
